import React, { useContext, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../AppContext';
import { Link } from 'react-router-dom';

const Logo: React.FC = () => {
  const navigate = useNavigate();
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('Step4 must be used within an AppProvider');
  }

  const { appData, updateAppData } = context;
  const [appBarPreview, setAppBarPreview] = useState(appData.appBarLogo || 'https://build.zas.media/images/logo.png');
  const [logoPreview, setLogoPreview] = useState(appData.logo || 'https://build.zas.media/images/logo.png');
  const [radioLogoPreview, setRadioLogoPreview] = useState(appData.radioLogo || 'https://build.zas.media/images/logo.png');

  const [previews, setPreviews] = useState({
    appBarLogo: appData.appBarLogo || 'https://build.zas.media/images/logo.png',
    logo: appData.logo || 'https://build.zas.media/images/logo.png',
    radioLogo: appData.radioLogo || 'https://build.zas.media/images/logo.png'
  });

  const initialValues = {
    logo: appData.logo || '',
    appBarLogo: appData.appBarLogo || '',
    radioLogo: appData.radioLogo || '',
  };

  const validationSchema = Yup.object({
    appBarLogo: Yup.string().required('O logotipo na barra superior é obrigatório'),
    logo: Yup.string().required('O logotipo na intro é obrigatório'),
    radioLogo: Yup.string().when([], {
      is: () => !["web-church-tv", "web-church", "web-tv"].includes(appData.type ?? ''),
      then: (schema) => schema.required('O logotipo da rádio é obrigatório'),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const handleUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: keyof typeof previews,
    setFieldValue: (field: string, value: string) => void
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'image/png') {
      alert('Por favor, envie um arquivo PNG.');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert('O arquivo deve ter no máximo 2MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setPreviews(prev => ({ ...prev, [field]: result }));
      setFieldValue(field, result);
      updateAppData({ [field]: result });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="container">
      <div className="grid grid-cols-6 gap-4">
        <div className="col-span-2 content content9">
          <p className="font-semibold tracking-tight text-pretty text-gray-900 sm:text-2xl lg:text-balance">
            Logotipos
          </p>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              updateAppData(values);
              navigate(`/${appData.id}/cores`);
            }}
          >
            {({ setFieldValue }) => (
              <Form className='h-full'>
                <div className='flex flex-col h-full justify-between'>
                  <div>
                    <div className="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3 mt-6" role="alert">
                      <p className="font-bold">Importante</p>
                      <p className="text-sm">Envie imagens no formato PNG e em alta resolução, com um tamanho máximo de 2MB por arquivo.</p>
                    </div>
                    {['appBarLogo', 'logo', 'radioLogo']
                    .filter(field => !(field === 'radioLogo' && ["web-church-tv", "web-church", "web-tv"].includes(appData.type ?? '')))
                    .map((field, index) => (
                      <div className="col-span-full mt-10" key={index}>
                        <label htmlFor={field} className="block font-bold text-base text-gray-900">
                          {field === 'appBarLogo' && 'Logotipo na barra superior do app'}
                          {field === 'logo' && 'Logotipo na intro do app'}
                          {field === 'radioLogo' && 'Logotipo da rádio'}
                        </label>
                        <input
                          type="file"
                          accept="image/png"
                          className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-50 mt-6 w-full"
                          onChange={(event) => handleUpload(event, field as keyof typeof previews, setFieldValue)}
                        />
                        <ErrorMessage name={field} component="p" className="text-red-500 text-xs italic mt-2" />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-6">
                    <Link
                      to={`/${appData.id}/titulo`}
                      className="bg-primary text-white px-6 py-3 text-xl rounded-md flex items-center space-x-2"
                    >
                      <i className="fas fa-arrow-left"></i>
                      <span>Título</span>
                    </Link>
                    <button
                      type="submit"
                      className="bg-primary text-white px-6 py-3 text-xl rounded-md flex items-center space-x-2"
                    >
                      <span>Cores</span>
                      <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
        <div className="col-span-4 relative">
          <svg fill="none" className="dash"><defs><pattern x="0" y="0" id="pattern-dfa52911-fec3-4817-ac8e-19f444cb2809" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M-3 13 15-5M-5 5l18-18M-1 21 17 3"></path></pattern></defs><rect fill="url(#pattern-dfa52911-fec3-4817-ac8e-19f444cb2809)" width="100%" height="100%" stroke="none"></rect></svg>
          <div className='dash-over w-full p-4'>
            <div className='flex justify-center'>
              <div className="mockup-crop bar">
                <div className="mockup-phone phone">
                  <div className="camera">
                    <div className='text-white text-center'>Barra superior</div>
                  </div>
                  <div className="display">
                    <div className="artboard phone-7 bg-white">
                    <div className="topbar">
                        <span>12:00</span>
                        <span>
                          <i className="fas fa-wifi"></i>
                          <i className="fas fa-signal"></i>
                        </span>
                      </div>
                      <div className="appbar">
                        <img className="appbar-logo" src={previews.appBarLogo ?? 'https://build.zas.media/images/logo.png'} alt="App Bar" />
                        <span>
                          <i className="fas fa-search"></i>
                          <i className="fas fa-cog"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='flex justify-between'>
              <div className="mt-10">
                <div className="mockup-phone">
                  <div className="camera">
                    <div className='text-white text-center'>Intro</div>
                  </div>
                  <div className="display pb-10 app-bg">
                    <div className="artboard phone-1">
                      <div className='flex justify-center items-center app-intro'>
                        <img src={previews.logo ?? 'https://build.zas.media/images/logo.png'} className='app-logo' alt="App Bar" />
                      </div>
                     </div>
                  </div>
                </div>
              </div>
              {!["web-church-tv", "web-church", "web-tv"].includes(appData.type ?? '') && (
                <div className="mt-10">
                  <div className="mockup-phone">
                    <div className="camera">
                      <div className='text-white text-center'>Rádio</div>
                    </div>
                    <div className="display pb-10 app-bg">
                      <div className="artboard phone-1 app-mini">
                        <div className='flex flex-col justify-between h-full app-radio'>
                          <div className='flex flex-col justify-between h-full'>
                            <div className='flex justify-center items-center w-full mt-10 logo'>
                              <img src={previews.radioLogo ?? 'https://build.zas.media/images/logo.png'} className='app-radio-logo' alt="Rádio Logo" />
                            </div>
                            <div className='flex flex-col items-center'>
                              <div className='playing-now'>TOCANDO AGORA</div>
                              <div className='playing-title mt-2'>Web Rádio</div>
                            </div>
                            <div className="radio-controls">
                              <div className='controls'>
                                <div className='flex justify-between items-center'>
                                  <i className="fa-solid fa-music"></i>
                                  <div className='play'>
                                    <i className="fas fa-play"></i>
                                  </div>
                                  <i className="fa-solid fa-share-nodes"></i>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="navbar">
                            <div className="icon-box">
                              <i className="fas fa-home"></i>
                              Home
                            </div>
                            <div className="icon-box active svg">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                width="32"
                                height="32"
                              >
                                <path d="M2 9a2 2 0 0 1 2-2h7.585l4.707-4.707a1 1 0 1 1 1.414 1.414L14.414 7H20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9zm2 0v10h16V9H4zm8 5a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm-3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm6-4a1 1 0 0 1 1-1h3a1 1 0 1 1 0 2h-3a1 1 0 0 1-1-1zm0 3a1 1 0 0 1 1-1h3a1 1 0 1 1 0 2h-3a1 1 0 0 1-1-1zm0 3a1 1 0 0 1 1-1h3a1 1 0 1 1 0 2h-3a1 1 0 0 1-1-1z" />
                              </svg>
                              Rádio
                            </div>
                            <div className="icon-box">
                              <i className="fas fa-tv"></i>
                              TV
                            </div>
                            <div className="icon-box">
                              <i className="fas fa-play"></i>
                              Vídeos
                            </div>
                            <div className="icon-box">
                              <i className="fas fa-plus-circle"></i>
                              Mais
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Logo;
